import { Controller } from "react-hook-form";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import i18next from "i18next";
import {
  FIELD_REQUIRED,
  PRESS_ENTER_TO_ADD_OPTION,
} from "../../i18n/keysTranslations";
import { getReactHookFormNestedError } from "../../utils/errors";
import { useTranslationApp } from "../../lib/i18next";
import {
  CHIP_SEARCH_COMPONENT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../utils/colors";
import Chip from "@mui/material/Chip";

const filter = createFilterOptions();

const SelectCreatable = ({
  control,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  errors = {},
  label = "",
  name = "",
  id = name,
  value = [],
  fullWidth = true,
  helperText,
  disabled = false,
  placeholder = "",
  limitTags = 2,
  sx,
  className = "",
  color = "primary",
  variant = "filled",
  InputProps = {},
  InputLabelProps = {},
}) => {
  const { t } = useTranslationApp();
  const error = getReactHookFormNestedError(errors, name);

  return (
    <Controller
      control={control}
      name={name}
      id={name}
      rules={rules}
      defaultValue={value}
      render={({ field }) => (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <Autocomplete
            label={label}
            hasLabel={Boolean(label)}
            hiddenLabel={!label}
            {...field}
            multiple
            id={id}
            limitTags={limitTags}
            value={field.value || []}
            disabled={disabled}
            onChange={(_, newValue) => {
              // Handle both string values and objects with inputValue
              const processedValues = newValue.map((item) => {
                if (typeof item === "string") return item.toLowerCase();
                if (item.inputValue) return item.inputValue.toLowerCase();
                return item;
              });
              field.onChange(processedValues);
            }}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              const { inputValue } = params;

              // Show "Press enter to add option" message instead of the value
              if (inputValue !== "") {
                filtered.push({
                  inputValue,
                  label: t(PRESS_ENTER_TO_ADD_OPTION),
                });
              }

              return filtered;
            }}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                return option;
              }
              // Show original value for existing chips
              if (option.inputValue) {
                return option.inputValue;
              }
              return option;
            }}
            renderOption={(props, option) => (
              <li {...props}>{option.label || option}</li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  label={
                    typeof option === "string" ? option : option.inputValue
                  }
                  {...getTagProps({ index })}
                  sx={{
                    backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                  }}
                />
              ))
            }
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            options={[]}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                hiddenLabel={!label}
                fullWidth={fullWidth}
                placeholder={placeholder}
                error={!!error}
                variant={variant}
                color={color}
                className={className}
                InputProps={{
                  ...params.InputProps,
                  ...InputProps,
                }}
                InputLabelProps={InputLabelProps}
                sx={sx}
              />
            )}
          />
          {error?.message ? (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {error.message}
            </Typography>
          ) : null}
          {!error && helperText ? (
            <Typography variant="caption" color="textSecondary">
              {helperText}
            </Typography>
          ) : null}
        </Box>
      )}
    />
  );
};

export default SelectCreatable;
